/**
 * SEOProvider — Platform SEO Engine.
 *
 * - Fetch global SEO from `/api/public/seo` once
 * - Cache in context; components can fetch product/category-specific via helper
 * - Also inject verification meta tags on load
 * - Provides `setPageSEO()` for pages to declaratively override current SEO.
 *   A single central useEffect applies to DOM — avoids parent/child effect
 *   ordering issues that cause parent to overwrite child.
 */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const DEFAULT_SEO = {
  meta: { title: "", description: "", keywords: "", canonical_base: "", robots_index: true, robots_follow: true },
  og: { title: "", description: "", image: "", type: "website", site_name: "" },
  twitter: { title: "", description: "", image: "", card_type: "summary_large_image" },
  verification: { google: "", bing: "", facebook: "" },
  local_business: { enabled: true, opening_hours: "Mo-Su 08:00-20:00", price_range: "$$" },
  _branding: { name: "", short_name: "", tagline: "", logo: "", email: "", whatsapp: "", instagram: "", facebook: "", address: "", maps_url: "" },
};

const SEOContext = createContext({
  seo: DEFAULT_SEO,
  loading: true,
  reload: async () => {},
  pageOverride: null,
  setPageSEO: () => {},
});

export function SEOProvider({ children }) {
  const [seo, setSeo] = useState(DEFAULT_SEO);
  const [loading, setLoading] = useState(true);
  const [pageOverride, setPageOverride] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/public/seo");
      if (r.data) setSeo(r.data);
    } catch { /* keep defaults */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Inject verification meta tags
  useEffect(() => {
    if (typeof document === "undefined") return;
    const v = seo.verification || {};
    const upsert = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    upsert("google-site-verification", v.google);
    upsert("msvalidate.01", v.bing);
    upsert("facebook-domain-verification", v.facebook);
  }, [seo]);

  const setPageSEO = useCallback((payload) => {
    setPageOverride(payload);
  }, []);

  const value = useMemo(
    () => ({ seo, loading, reload: load, pageOverride, setPageSEO }),
    [seo, loading, load, pageOverride, setPageSEO],
  );
  return <SEOContext.Provider value={value}>{children}</SEOContext.Provider>;
}

export function useSEO() { return useContext(SEOContext); }

/** Fetch product-specific SEO (returns null if not found) */
export async function fetchProductSEO(slug) {
  try {
    const r = await api.get(`/public/seo/product/${slug}`);
    return r.data || null;
  } catch { return null; }
}

/** Fetch category-specific SEO */
export async function fetchCategorySEO(slug) {
  try {
    const r = await api.get(`/public/seo/category/${slug}`);
    return r.data || null;
  } catch { return null; }
}
